import { type ApolloError, gql } from "@apollo/client";
import { useMutation } from "@apollo/client";
import { useCallback } from "react";

import type { LineItem } from "@/apis/gql/types";
import { useTenant } from "@/providers/session";

import { INVOICE_QUERY } from "../invoices/get";
import { INVOICES_QUERY } from "../invoices/get-all";

type SyncLineItemsData = {
  update: {
    lineItemIds: string[];
    changes: Partial<LineItem>;
  }[];
  del: {
    lineItemIds: string[];
  };
  create: {
    lineItems: Partial<LineItem>[];
  };
};

export const useSyncLineItems = ({
  invoiceId,
}: {
  invoiceId: string;
}): [
  (
    data: SyncLineItemsData,
    { signal }: { signal?: AbortSignal },
  ) => Promise<LineItem[]>,
  {
    data: any;
    loading: boolean;
    error?: ApolloError;
  },
] => {
  const { tenantId } = useTenant();

  const [syncLineItems, { loading, error, data }] = useMutation(
    SYNC_LINE_ITEMS_MUTATION,
    {
      variables: {
        tenantId,
        invoiceId,
      },
      refetchQueries: [INVOICES_QUERY, INVOICE_QUERY],
      update: (cache, { data }) => {
        if (data?.syncLineItems) {
          cache.evict({ fieldName: "invoices" });
          cache.gc();
        }
      },
    },
  );

  const handleSyncLineItems = useCallback(
    async (
      { update, del, create }: SyncLineItemsData,
      { signal }: { signal?: AbortSignal },
    ) => {
      if (!tenantId) {
        throw new Error("Tenant ID is required");
      }

      const { data } = await syncLineItems({
        variables: {
          update,
          del,
          create,
        },
        context: {
          fetchOptions: {
            signal,
          },
        },
      });

      return data.syncLineItems ?? [];
    },
    [syncLineItems, tenantId],
  );

  return [handleSyncLineItems, { loading, error, data }];
};

export const SYNC_LINE_ITEMS_MUTATION = gql`
  mutation SyncLineItems(
    $tenantId: String!
    $invoiceId: String!
    $update: [LineItemUpdateInput]
    $del: LineItemDeleteInput
    $create: LineItemCreateInput
  ) {
    syncLineItems(
      tenantId: $tenantId
      invoiceId: $invoiceId
      update: $update
      del: $del
      create: $create
    ) {
      lineItemDetailsId
      lineItem {
        lineItemId
        costCenterId
        aiDescription
        amount
        nettoAmount
        invoiceId
        name
        vatRate
        startDate
        endDate
        accountJustification
        index
        description
        accountConfidence
        vatRateConfidence
        accountReviewed
        accountId
        amountConfidence
        taxCodeId
        buJustification
        taxCodeConfidence
        bookingText
        bookingTextManuallyUpdated
        createdAt
        createdBy
        unlinked
      }
      costCenter {
        costCenterId
        name
        description
        identifier
      }
      expenseAccount {
        accountId
        accountNo
        accountName
        accountDescription
        accountType
        automatic
        parentAccountId
        min
        max
        buExport
        serviceDateExport
        faktor2
      }
      taxCode {
        taxCodeId
        name
        description
        datevBu
        vatRate
      }
    }
  }
`;
